import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {DataSource} from '../../../replete';

import {CustomComponent} from '../../common/components';
import {Icon} from '../../common/components/Icon';
import {window, isMobile, isStaticImage, isSticker} from '../../common/util';
import {getMediaType} from './GifConstants';

import type {APIService} from '../../common/services/apiService';

import './Gif.scss';

const MEDIA_SIZES = {
    'gif': ['nanogif', 'tinygif', 'tinywebp', 'mediumgif', 'gif'],
    'meme': [
        'tinygif_transparent',
        'tinywebp_transparent',
        'gif_transparent',
    ],
    'sticker': [
        'tinygif_transparent',
        'tinywebp_transparent',
        'gif_transparent',
    ],
};

export const MEDIA_QUALITY = Object.freeze({
    FALLBACK: 'fallback',
    TINY: 'tiny',
    SD: 'SD',
    HD: 'HD',
    VIDEO: 'video',
    WEBP: 'webp',
    TINYWEBP: 'tinywebp',
});

export const MEDIA_QUALITY_MAPPING = Object.freeze({
    'fallback': {
        gif: 'tinygif',
        meme: 'tinygif',
        sticker: 'originalgif',
    },
    'tiny': {
        gif: 'tinygif',
        meme: 'tinygif',
        sticker: 'tinygif_transparent',
    },
    'SD': {
        gif: 'mediumgif',
        meme: 'mediumgif',
        sticker: 'tinygif_transparent',
    },
    'HD': {
        gif: 'gif',
        meme: 'gif',
        sticker: 'gif_transparent',
    },
    'video': {
        gif: ['mp4', 'webm'],
        meme: null, // N/A to still images
        sticker: null, // mp4 does not support transparency
    },
    'webp': {
        gif: 'webp',
        meme: 'webp',
        sticker: 'webp_transparent',
    },
    'tinywebp': {
        gif: 'tinywebp',
        meme: 'tinywebp',
        sticker: 'tinywebp_transparent',
    },
});

// TODO:
// Need this for tags too

function selectUrl(
    gif: api.Post, size: string, apiService: APIService): string {
    const media = gif.media_formats;
    const mediaType = getMediaType(gif);
    // Sometimes media formats are missing.

    if (Gif.hasFormat(gif, size)) {
        return Gif.getFormatUrlAuthed(gif, size, apiService);
    } else {
        // NOTE: the GifMakerEntrypoint card in searchpage GifLists only has
        // the 'tinygif' format
        const fallbackSize = MEDIA_QUALITY_MAPPING[
            MEDIA_QUALITY.FALLBACK][mediaType];
        if (Gif.hasFormat(gif, fallbackSize)) {
            return Gif.getFormatUrlAuthed(gif, fallbackSize, apiService);
        } else {
            console.error(
                `Missing Format: ${
                    size}, no ${fallbackSize} fallback for ${gif.id}`,
            );
            // NB: Deliberately returning an invalid URL to ensure a broken
            // image icon is rendered.
            return `""`;
        }
    }
}

export class DataSaverModeDataSource extends DataSource {
    // a bug in replete requires everything to have a DataSource
    getInitial() {
        return false;
    }
}
export class GifImgSizesDataSource extends DataSource {
    key([gif]) {
        return gif.id;
    }
    getInitial([gif]) {
        const val = {};
        this.set([gif], val);
        return val;
    }
}


class GifLikeComponent extends CustomComponent {
    @autobind
    getSources(gif: api.Post) {
        const srcSet = [];
        const tinygifFormat = Gif.getFormat(gif, 'tinygif');
        const tinywebpFormat = Gif.getFormat(gif, 'tinywebp');
        const tinymp4Format = Gif.getFormat(gif, 'tinymp4');
        if (tinymp4Format && tinymp4Format.size < tinygifFormat.size) {
            // No point in adding mp4 if it's larger than a gif. This
            // happens when the tinymp4 is significantly wider than the
            // tinygif.
            // Note: mp4 has to be specified using a <source/> element
            // so that we can provide a mimetype hint upfront.
            // Otherwise browsers may fetch the URL in order to find
            // out what it is, which defeats the purpose.
            srcSet.push({
                gif,
                mimetype: 'video/mp4',
                formatStr: 'tinymp4',
                smallScreenOnly: tinymp4Format.dims[0] < tinygifFormat.dims[0],
            });
        }
        if (tinywebpFormat) {
            srcSet.push({
                gif,
                mimetype: 'image/webp',
                formatStr: 'tinywebp',
                smallScreenOnly: tinywebpFormat.dims[0] < tinygifFormat.dims[0],
            });
        }
        return srcSet;
    }
    @autobind
    renderSource({gif, mimetype, formatStr, smallScreenOnly}) {
        const format = Gif.getFormat(gif, formatStr);
        const url = selectUrl(gif, formatStr, this.context.apiService);
        return <source
            media={smallScreenOnly ? '(max-width:840px)' : undefined}
            type={mimetype}
            srcSet={`${url} ${format.dims[0]}w`}
        />;
    }
}


export class Gif extends GifLikeComponent {
    static debugNamespace = 'Gif';

    isMeme?: boolean;
    isSticker?: boolean;
    mediaType?: string;
    size?: string;
    inViewport?: boolean;
    hasBeenOutsideViewport?: boolean;
    adTrackingStarted?: boolean;
    element?: HTMLImageElement|null;
    videoElement?: HTMLVideoElement|null;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        const debug = this.extendDebug('constructor');
        this.state = {
            ...this.state,
            touched: false,
        };
        this.handleNewGif(this.props, this.state);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.trackVisibility);
    }

    componentWillUpdate(nextProps: jsx.Props, nextState: jsx.State) {
        if (nextProps.gif.id !== this.props.gif.id) {
            window.removeEventListener('scroll', this.trackVisibility);
            this.handleNewGif(nextProps, nextState);
        }
    }

    handleNewGif(props: jsx.Props, state: jsx.State) {
        const debug = this.extendDebug('handleNewGif');
        const {gif, quality} = props;
        this.isMeme = isStaticImage(gif);
        this.isSticker = isSticker(gif);
        this.mediaType = getMediaType(gif);


        this.inViewport = false;
        this.hasBeenOutsideViewport = true;
        this.adTrackingStarted = false;

        if (this.element) {
            // Instantly clear previous image. Prevents it from showing during
            // transition to new image.
            this.element.src = '';
        }

        if (!process.env.BROWSER || quality === MEDIA_QUALITY.VIDEO) {
            this.onCompletedGif();
        }
    }

    getSize(quality: string) {
        return MEDIA_QUALITY_MAPPING[quality][this.mediaType];
    }

    onCompletedGif() {
        process.env.BROWSER && this.props.onCompletedGif?.();
    }

    setLoadedSrc(gif: api.Post, size: string) {
        // Only update if the GIF ID still matches, since it could have
        // changed.
        // Don't need to update store if gif is a card. (eg. gifmaker entry
        // point)
        if (!gif.isCard) {
            if (MEDIA_SIZES[this.mediaType].includes(size)) {
                const cachedSizes = this.context.store.get(
                    'ui.gifSizes.*', [gif]) || {};
                cachedSizes[size] = true;
                this.context.store.set(`ui.gifSizes.*`, [gif], cachedSizes);
            }
        }
    }

    @autobind
    setElement(element: HTMLImageElement) {
        this.element = element;
        if (element) {
            element.addEventListener('load', this.onImgLoad);
        }
    }

    @autobind
    setVideoElement(element: HTMLVideoElement) {
        this.videoElement = element;
        if (element) {
            element.addEventListener('canplay', this.trackIfAd);
        }
    }

    @autobind
    onImgLoad() {
        this.trackIfAd();
        this.onCompletedGif();
    }

    @autobind
    trackIfAd() {
        if (this.adTrackingStarted) {
            return;
        }
        this.adTrackingStarted = true;
        // Only track ads (GIFs with source_id and feature_info)
        if (this.props.gif.source_id && this.props.gif.feature_info) {
            this.trackVisibility();
            window.addEventListener('scroll', this.trackVisibility);
        }
    }

    @autobind
    trackVisibility() {
        /*
        When an ad has been completely outside the viewport (due to having not
        been rendered yet, or being scrolled out of view) and then becomes
        completely visible again, record a view. Being partially out of view
        doesn't count as either being in the viewport or completely outside it.
        */
        let element;
        if (this.element) {
            element = this.element;
        } else if (this.videoElement) {
            element = this.videoElement;
        } else {
            this.inViewport = false;
            return;
        }

        const rect = element.getBoundingClientRect();
        const navbar = window.document.getElementsByClassName('TopBar');
        const navbarOffset = navbar.length ? navbar[0].clientHeight : 0;
        const footerOffset = 0;

        // ad completely within viewport
        if (rect.top >= navbarOffset &&
            rect.left >= 0 &&
            rect.bottom <= window.innerHeight - footerOffset &&
            rect.right <= window.innerWidth) {
            this.inViewport = true;
        } else {
            this.inViewport = false;
        }
        // ad completely outside viewport
        if (rect.top > window.innerHeight - footerOffset ||
            rect.left > window.innerWidth ||
            rect.bottom < navbarOffset ||
            rect.right < 0) {
            this.hasBeenOutsideViewport = true;
        }

        if (this.hasBeenOutsideViewport && this.inViewport) {
            this.hasBeenOutsideViewport = false;
            this.context.apiService.registerAdinViewport(this.props.gif);
        }
    }

    static getStickerPadding() {
        return isMobile() ? 8 : 12;
    }

    static hasFormat(gif: api.Post, format: string): boolean {
        return Boolean(
            gif && gif.media_formats && gif.media_formats[format]);
    }

    static getFormat(gif: api.Post, format: string): api.Media|undefined {
        return Gif.hasFormat(gif, format) ?
            gif.media_formats[format] : undefined;
    }

    static getFormatParam<Param extends keyof api.Media>(
        gif: api.Post,
        format: string,
        param: keyof api.Media): api.Media[Param]|undefined {
        return Gif.hasFormat(gif, format) ?
            gif.media_formats[format][param] : undefined;
    }

    /**
     * Returns the url for the given format.
     */
    static getFormatUrl(gif: api.Post, format: string): string|undefined {
        return Gif.getFormatParam<'url'>(gif, format, 'url');
    }

    /**
     * Returns the url for the given format. May add access_token if the url is
     * for a /v2/ URL.
     */
    static getFormatUrlAuthed(
        gif: api.Post,
        format: string,
        apiService: APIService): string|undefined {
        return apiService.addAuthToImgUrlIfV2(Gif.getFormatUrl(gif, format));
    }

    static getFormatDims(
        gif: api.Post,
        format: string): [number, number]|undefined {
        return Gif.getFormatParam<'dims'>(gif, format, 'dims');
    }
    render() {
        const debug = this.extendDebug('render');
        let {
            gif,
            quality,
            width,
            height,
            style,
            mediaStyle,
            muted,
            inList,
            ...otherProps
        } = this.props;

        const renderSize = quality ? this.getSize(quality) : 'auto';
        debug('sizes', {quality, renderSize});
        this.setLoadedSrc(gif, renderSize);

        // Don't render this attribute
        delete (otherProps as any)['inUniversalSearch'];
        style = style || {};
        mediaStyle = mediaStyle || {};
        if (gif.bg_color) {
            mediaStyle['background-color'] = gif.bg_color;
        }

        if (quality === MEDIA_QUALITY.VIDEO ||
            gif.media_formats.tinygif.isActuallyVideo) {
            if (this.videoElement) {
                debug('has videoElement');
                // Needed in case source or other settings changed in-place.
                window.setTimeout(() => {
                    if (this.videoElement) {
                        const src = this.videoElement.currentSrc;
                        if (src !== Gif.getFormatUrlAuthed(gif, 'mp4', this.context.apiService) && src !== Gif.getFormatUrlAuthed(gif, 'webm', this.context.apiService)) {
                            debug('src doesn\'t match');
                            this.videoElement.load();
                        }
                    }
                }, 0);
            }
            // Note: preload=none tends to get ignored.
            debug('video render', width, height);
            this.context.response.preload(Gif.getFormatUrlAuthed(gif, 'mp4', this.context.apiService), 'video', false);

            return (
                <div
                    className={`Gif`}
                    style={style}
                    onTouchStart={() => {
                        this.setState({touched: true});
                    }}
                    onTouchEnd={() => {
                        this.setState({touched: false});
                    }}
                    onTouchCancel={() => {
                        this.setState({touched: false});
                    }}
                    {...otherProps}
                >
                    <video
                        ref={this.setVideoElement}
                        muted={muted !== false}
                        width={width}
                        height={height}
                        loop={true}
                        autoPlay={true}
                        preload={'auto'}
                        style={mediaStyle}
                        // iOS bullshit to prevent video from automatically
                        // entering full screen
                        playsInline={true}
                    >
                        {Gif.hasFormat(gif, 'mp4') && <source
                            src={Gif.getFormatUrlAuthed(gif, 'mp4', this.context.apiService)}
                            type="video/mp4"
                        />}
                        {Gif.hasFormat(gif, 'webm') && <source
                            src={Gif.getFormatUrlAuthed(gif, 'webm', this.context.apiService)}
                            type="video/webm"
                        />}
                    </video>
                </div>
            );
        } else {
            // See if onLoad event can be used for image load detection.
            let src = selectUrl(
                gif, renderSize, this.context.apiService);

            let mediaWidth = width;
            let mediaHeight = height;
            const makeSquare = !this.props.inUniversalSearch &&
                (this.isSticker ||
                    (this.isMeme && this.props.inList)) &&
                    Gif.hasFormat(gif, renderSize);
            if (makeSquare) {
                const dims = Gif.getFormatDims(gif, renderSize) ||
                    [100, 100];
                const aspectRatio = dims[0] / dims[1];
                const padding = Gif.getStickerPadding();
                if (width && height) {
                    width = width - padding * 2;
                    height = height - padding * 2;
                    if (aspectRatio >= 1) {
                        mediaWidth = width;
                        mediaHeight = height / aspectRatio;
                    } else {
                        mediaWidth = width * aspectRatio;
                        mediaHeight = width;
                    }
                }
                mediaStyle['background-color'] = 'unset';
            }
            let srcSet = [];
            if (src && renderSize === MEDIA_QUALITY.TINYWEBP) {
                src = selectUrl(
                    gif, MEDIA_QUALITY.TINY, this.context.apiService);
                srcSet = this.getSources(gif);
            }

            const fetchPriority = this.props.inList ? undefined : 'high';
            const loading = this.props.inList ? 'lazy' : undefined;
            const className = `${this.isSticker ?
                'Sticker' : this.isMeme ?
                    'Meme' : 'Gif'}${this.state.touched ? ' touched' : ''}`;
            return (
                <div
                    className={className}
                    style={style}
                    onTouchStart={() => {
                        this.setState({touched: true});
                    }}
                    onTouchEnd={() => {
                        this.setState({touched: false});
                    }}
                    onTouchCancel={() => {
                        this.setState({touched: false});
                    }}
                    {...otherProps}
                >
                    {srcSet.length === 0 ? <img
                        ref={this.setElement}
                        src={src}
                        width={mediaWidth}
                        height={mediaHeight}
                        alt={src ? gif.content_description : ''}
                        style={mediaStyle}
                        fetchPriority={fetchPriority}
                        loading={loading}
                    /> : <picture>
                        {srcSet.map(this.renderSource)}
                        <img
                            ref={this.setElement}
                            src={src}
                            width={mediaWidth}
                            height={mediaHeight}
                            alt={src ? gif.content_description : ''}
                            style={mediaStyle}
                            fetchPriority={fetchPriority}
                            loading={loading}
                        />
                    </picture>}
                </div>
            );
        }
    }
}


export class Card extends GifLikeComponent {
    static debugNamespace = 'Card';

    render() {
        let {
            gif, width, height, style, mediaStyle, inList, ...otherProps
        } = this.props;
        // Don't render this attribute
        delete (otherProps as any)['inUniversalSearch'];
        const gifSize = 'tinygif';
        const widthRatio = gif.altDims[0] / gif.media_formats[gifSize].dims[0];
        const heightRatio = gif.altDims[1] / gif.media_formats[gifSize].dims[1];
        style = style || {};
        style['font-size'] = `${height / 20}px`;
        style['height'] = `${height}px`;
        mediaStyle = mediaStyle || {};
        if (gif.bg_color) {
            mediaStyle['background-color'] = gif.bg_color;
        }
        const src = selectUrl(gif, gifSize, this.context.apiService);
        const srcSet = this.getSources(gif);

        return (
            <div
                className={'Gif Card'}
                style={style}
                {...otherProps}
            >
                {gif.header}
                <picture>
                    {srcSet.map(this.renderSource)}
                    <img
                        src={src}
                        width={width * widthRatio}
                        height={height * heightRatio}
                        alt={src ? gif.content_description : ''}
                        style={mediaStyle}
                        loading='lazy'
                    />
                </picture>
                {gif.footer}
            </div>
        );
    }
}
